// SETTINGS V2 — Issue guest-pass / add demo user dialog (TA3; rewired from
// InviteTemporaryDialogV2, extended by DEMO_USER_TYPE_SPEC F1).
//
// Owner-facing access issuance. Two pass types share one form:
//   • Guest technician — POST /v1/users/guest-passes (Machinery:Write, unchanged).
//   • Demo visitor     — POST /v1/users/guest-passes/demo (Users:Admin), which
//     mints a Worker-privilege principal confined to demo-allowlisted tenants.
// The type selector only renders for Users:Admin, so the Manager-facing dialog is
// byte-identical to what shipped in TA3.
//
// Demo specifics required by the spec / security review:
//  • Server refusals are surfaced VERBATIM inline (spec §3 point 1), never mapped
//    to a generic toast — an operator minting against a non-allowlisted company
//    must learn exactly why.
//  • Demo passes send NO email (owner ruling 2026-08-04) — the screen is the only
//    channel. The address field is therefore labelled as a record of who the pass
//    was for, and no copy anywhere on the demo path may promise delivery.
//  • On success the form is REPLACED by DemoPassHandoffV2 rather than closing with
//    a toast — the owner is mid-call and needs the PIN and its expiry on screen.
//
// SettingsV2DialogShell consumer (controlled state, NOT react-hook-form — SP2
// ruling). Machines fetched on open via customerMachineryApi. Inline styles +
// scoped CSS-var tokens + v2 atoms. No MUI/sx/dt/Tailwind. Open-state lives in
// settingsV2UiSlice.openDialog ∈ {"issueGuestPass","issueDemoPass"}. SEC-REQ-2:
// fetch only in the open effect.
import React, { useEffect, useState } from "react";
import type { TFunction } from "i18next";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import customerMachineryApi from "../../../machinery/api/customerMachineryApi";
import { Machine } from "../../../machinery/models/models";
import { IssueGuestPass } from "../../models/guestPassModels";
import {
  issueGuestPass,
  issueDemoPass,
  getGuestPasses,
  clearDemoMintError,
  clearLastDemoPass,
} from "../../guestPassesSlice";
import { closeDialog } from "../settingsV2UiSlice";
import { SErrorCallout } from "../atoms";
import SettingsV2DialogShell from "./SettingsV2DialogShell";
import PassTypeSelectorV2, { PassType } from "./PassTypeSelectorV2";
import GuestPassFormFieldsV2 from "./GuestPassFormFieldsV2";
import DemoPassHandoffV2 from "./DemoPassHandoffV2";
import {
  buildGuestPassFormErrors,
  isGuestPassFormValid,
  GuestPassFormValues,
} from "./guestPassFormUtils";

const EMPTY: GuestPassFormValues = {
  machineInternalNumber: "",
  guestEmail: "",
  guestName: "",
  duration: "1",
  visitPurpose: "",
};

const VISIT_PURPOSE_MAX = 200;

const IssueGuestPassDialogV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t, getCurrentLanguage } = useTranslation("settings");
  const { translate: tAccount } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();
  // Mirrors the server gate on POST /v1/users/guest-passes/demo
  // (RequireModulePermission(Users, Admin)) — Managers never see the demo option.
  const { canAdmin: canMintDemo } = usePermission(Modules.Users);

  const openDialogId = useAppSelector((s) => s.settingsV2Ui.openDialog);
  const open = openDialogId === "issueGuestPass" || openDialogId === "issueDemoPass";
  const loading = useAppSelector((s) => s.guestPasses.loading);
  const demoError = useAppSelector((s) => s.guestPasses.demoError);
  const mintedDemoPass = useAppSelector((s) => s.guestPasses.lastDemoPass);

  const [form, setForm] = useState<GuestPassFormValues>(EMPTY);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [machines, setMachines] = useState<Machine[]>([]);
  const [passType, setPassType] = useState<PassType>("guest");

  const isDemo = passType === "demo" && canMintDemo;
  const showHandoff = isDemo && !!mintedDemoPass;

  const setField = (k: keyof GuestPassFormValues) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k: string) => setTouched((p) => ({ ...p, [k]: true }));

  // Seed/reset on open. The entry point picks the type: the Team menu's demo item
  // opens "issueDemoPass"; anything else lands on the guest default.
  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setTouched({});
      setPassType(openDialogId === "issueDemoPass" && canMintDemo ? "demo" : "guest");
      dispatch(clearDemoMintError());
      dispatch(clearLastDemoPass());
    }
  }, [open, openDialogId, canMintDemo, dispatch]);

  // Machines on open (silent failure → empty picker, parity with the roles fetch).
  useEffect(() => {
    if (!open) return;
    let active = true;
    customerMachineryApi
      .getMachines({})
      .then((grouped) => {
        if (!active) return;
        const flat = Object.values(grouped || {}).flat();
        setMachines(flat);
      })
      .catch(() => {
        if (active) setMachines([]);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const handleClose = () => {
    dispatch(closeDialog());
    // Drop the minted pass (and any PIN it carried) from memory on close.
    dispatch(clearLastDemoPass());
    dispatch(clearDemoMintError());
    setForm(EMPTY);
    setTouched({});
    // The list behind the dialog must show the new row + DEMO badge.
    if (mintedDemoPass) dispatch(getGuestPasses());
  };

  const isFormValid = isGuestPassFormValid(form, VISIT_PURPOSE_MAX);
  const errors = buildGuestPassFormErrors({
    values: form,
    touched,
    visitPurposeMax: VISIT_PURPOSE_MAX,
    requiredText: tCommon("forms.required") as string,
    invalidEmailText: tAccount("invalidEmail") as string,
    maxLengthText: tCommon("forms.maxLength", { max: VISIT_PURPOSE_MAX }) as string,
  });

  const machineOptions = machines.map((m) => ({
    value: m.internalNumber,
    label: `${m.model} · ${m.internalNumber}`,
  }));

  // Hour presets carry an "h" suffix ("2h"); bare numbers stay days ("1" = 1 day)
  // so the existing default in EMPTY is untouched by the hour options.
  const durationOptions = [
    { value: "2h", label: t("guestPass.duration2Hours") as string },
    { value: "4h", label: t("guestPass.duration4Hours") as string },
    { value: "8h", label: t("guestPass.duration8Hours") as string },
    { value: "1", label: t("guestPass.duration1Day") as string },
    { value: "3", label: t("guestPass.duration3Days") as string },
    { value: "7", label: t("guestPass.duration7Days") as string },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      machineInternalNumber: true,
      guestEmail: true,
      guestName: true,
      visitPurpose: true,
    });
    if (!isFormValid || loading) return;

    // "2h" → hours; a bare number → days. The API takes an explicit expiresAt.
    const raw = form.duration || "1";
    const hours = raw.endsWith("h")
      ? Number(raw.slice(0, -1)) || 24
      : (Number(raw) || 1) * 24;
    const expiresAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();

    const model: IssueGuestPass = {
      machineInternalNumber: form.machineInternalNumber,
      guestEmail: form.guestEmail.trim(),
      guestName: form.guestName.trim(),
      expiresAt,
      // EMAIL-LANG-WIRING: localizes the guest invite email ({{SourceLanguage}} branches).
      language: getCurrentLanguage(),
    };
    if (form.visitPurpose.trim()) model.visitPurpose = form.visitPurpose.trim();

    if (isDemo) {
      // Stay open on success — the handoff panel takes over the body. Failure
      // keeps the form (and the operator's input) with the refusal inline.
      await dispatch(issueDemoPass(model));
      return;
    }

    const result = await dispatch(issueGuestPass(model));
    if (issueGuestPass.fulfilled.match(result)) {
      dispatch(closeDialog());
      setForm(EMPTY);
      setTouched({});
      dispatch(getGuestPasses());
    }
  };

  const submitLabel = isDemo
    ? loading
      ? t("guestPass.demoCreating")
      : t("guestPass.demoCreate")
    : loading
    ? t("guestPass.sending")
    : t("guestPass.sendPass");

  const footer = showHandoff ? (
    <button
      type="button"
      className="btn btn-primary"
      onClick={handleClose}
      style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
    >
      {t("guestPass.handoffDone") as string}
    </button>
  ) : (
    <>
      <button type="button" className="btn btn-ghost" onClick={handleClose}>
        {tCommon("buttons.cancel") as string}
      </button>
      <button
        type="submit"
        form="issueGuestPassFormV2"
        className="btn btn-primary"
        disabled={!isFormValid || loading}
        style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
      >
        {submitLabel as string}
      </button>
    </>
  );

  const titleKey = showHandoff
    ? "guestPass.handoffTitle"
    : isDemo
    ? "guestPass.demoDialogTitle"
    : "guestPass.dialogTitle";
  const subKey = showHandoff
    ? "guestPass.handoffSub"
    : isDemo
    ? "guestPass.demoDialogSub"
    : "guestPass.dialogSub";
  const footerNoteKey = isDemo ? "guestPass.demoDialogFooter" : "guestPass.dialogFooter";

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={handleClose}
      width={600}
      title={t(titleKey) as string}
      sub={t(subKey) as string}
      footerNote={showHandoff ? undefined : (t(footerNoteKey) as string)}
      labelledById="sv2-issue-guest-pass-title"
      busy={loading}
      footer={footer}
    >
      {showHandoff && mintedDemoPass ? (
        <DemoPassHandoffV2 pass={mintedDemoPass} />
      ) : (
        <form
          id="issueGuestPassFormV2"
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          {canMintDemo && (
            <PassTypeSelectorV2
              label={t("guestPass.typeLabel") as string}
              value={passType}
              onChange={(v) => {
                setPassType(v);
                dispatch(clearDemoMintError());
              }}
              guestLabel={t("guestPass.typeGuest") as string}
              guestHint={t("guestPass.typeGuestHint") as string}
              demoLabel={t("guestPass.typeDemo") as string}
              demoHint={t("guestPass.typeDemoHint") as string}
              stacked={isPhone}
            />
          )}

          {/* Server refusal, verbatim (spec §3 point 1). */}
          {isDemo && !!demoError && (
            <SErrorCallout
              title={t("guestPass.demoRefusedTitle") as string}
              body={demoError}
            />
          )}

          <GuestPassFormFieldsV2
            values={form}
            onFieldChange={(k, v) => setField(k)(v)}
            onFieldTouched={markTouched}
            errors={errors}
            machineOptions={machineOptions}
            durationOptions={durationOptions}
            visitPurposeMax={VISIT_PURPOSE_MAX}
            isDemo={isDemo}
            isPhone={isPhone}
            t={t as TFunction}
            tAccount={tAccount as TFunction}
          />
        </form>
      )}
    </SettingsV2DialogShell>
  );
};

export default IssueGuestPassDialogV2;
